import type { LocalOrSavedFile } from '@frontend/api-types';
import { getNameFromFile, getTypeFromFile } from '@frontend/forms';
import { Fragment, useState } from '@frontend/react';
import { FilePreviewModal, Icon, Text, XStack, YStack } from '@frontend/web-react';
import { mergeAndConcatClasses } from '@frontend/web-utils';
import React from 'react';

type FilePreviewsListProps<T extends LocalOrSavedFile> = {
  EmptyComponent?: React.ComponentType;
  className?: string;
  files: T[];
  maxItemWidth?: number;
  onRemove?: (file: T) => void;
};

export const FilePreviewsList = <T extends LocalOrSavedFile>({
  className,
  EmptyComponent,
  files,
  maxItemWidth,
  onRemove,
}: FilePreviewsListProps<T>) => {
  const [previewedAttachment, setPreviewedAttachment] = useState<T>();

  return (
    <Fragment>
      {files.length ? (
        files.map((file, index) => {
          return (
            /* eslint-disable-next-line tailwindcss/no-arbitrary-value */
            <XStack
              className={mergeAndConcatClasses('rounded-lg hover-background-effect', className)}
              key={`${file.url}-${index}`}
              onClick={() => setPreviewedAttachment(file)}
              style={{ maxWidth: maxItemWidth }}
            >
              <XStack
                className="relative items-center border-borderPrimary rounded-lg border-1 gap-sm p-sm flex-1"
                enableHoverEffect
                key={file.url}
              >
                {onRemove ? (
                  <YStack
                    // eslint-disable-next-line tailwindcss/no-arbitrary-value,tailwindcss/no-custom-classname
                    className="bg-trueWhite p-px absolute right-[-6px] top-[-6px] cursor-pointer rounded-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove?.(file);
                    }}
                  >
                    <YStack className="bg-neutral-950 rounded-xl p-xs">
                      <Icon className="text-trueWhite" color="white" name="IconX" size={10} />
                    </YStack>
                  </YStack>
                ) : null}
                <Icon name="IconFile" size={20} />
                <YStack className="min-w-0">
                  <Text className="line-clamp-3 break-all" type="h6">
                    {getNameFromFile(file)}
                  </Text>
                  <Text color="secondary" type="caption">
                    {getTypeFromFile(file)}
                  </Text>
                </YStack>
              </XStack>
            </XStack>
          );
        })
      ) : EmptyComponent ? (
        <EmptyComponent />
      ) : null}
      <FilePreviewModal
        file={previewedAttachment}
        isOpen={!!previewedAttachment}
        onClose={() => setPreviewedAttachment(undefined)}
        title={previewedAttachment ? getNameFromFile(previewedAttachment) : ''}
      />
    </Fragment>
  );
};
